/**
 * URL routing for play.xiangqimaster.com.
 *
 * Every destination in the navigation is a real page: it owns a path, shows in
 * the address bar, can be linked, bookmarked, opened in a new tab and walked
 * with the browser's back/forward buttons. Nothing here is a tab.
 *
 * The app still renders inside one document — a game in progress, its clock,
 * the engine worker and the WebSocket must survive a click on "Learn" — so the
 * router swaps screens in place while history, title and canonical URL behave
 * exactly as they would for separate documents. nginx serves index.html for any
 * unknown path (`try_files`), which is what makes a deep link like
 * /learn/lessons/horse work on a cold load.
 *
 * Two rules keep this honest:
 *  - every screen declares its own URL by calling `page()` as it renders, so a
 *    screen reached from a button lands on the same URL as a deep link to it;
 *  - a screen reached *because* the URL said so must not push that URL again,
 *    which is why `page()` only touches history when the path actually differs.
 */

export type Params = Record<string, string>;

export interface Route {
  /** Pattern with `:name` placeholders, e.g. "/player/:name". */
  path: string;
  /** Paints the screen. It is expected to call `page()` with its own URL. */
  render: (params: Params) => void;
}

export interface RouterOptions {
  routes: Route[];
  /** Rendered when no route matches — a real 404 page, not a redirect. */
  notFound: (path: string) => void;
  /** Runs before any in-app navigation (close menus, unlock audio…). */
  before?: () => void;
  /**
   * Guards the current screen. Returning false refuses the navigation — a game
   * in progress is a sticky room and only resignation leaves it.
   */
  canLeave?: (to: string) => boolean;
  /** Suffix for every document title. */
  brand: () => string;
  /** Reported once per rendered page (analytics). */
  onPage?: (path: string, title: string) => void;
}

let opts: RouterOptions | null = null;
/** True while rendering because the URL said so — a redirect then replaces. */
let dispatching = false;

/** Strip a trailing slash so "/play/" and "/play" are the same page. */
export function normalizePath(path: string): string {
  const p = (path || "/").split("#")[0]!.split("?")[0]!;
  return p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
}

/** Match a pattern against a path, returning its `:params` or null. */
export function matchPath(pattern: string, path: string): Params | null {
  const pp = pattern.split("/").filter(Boolean);
  const ap = normalizePath(path).split("/").filter(Boolean);
  if (pp.length !== ap.length) return null;
  const params: Params = {};
  for (let i = 0; i < pp.length; i++) {
    const seg = pp[i]!;
    if (seg.startsWith(":")) params[seg.slice(1)] = decodeURIComponent(ap[i]!);
    else if (seg !== ap[i]) return null;
  }
  return params;
}

function resolve(path: string): { route: Route; params: Params } | null {
  for (const r of opts!.routes) {
    const params = matchPath(r.path, path);
    if (params) return { route: r, params };
  }
  return null;
}

/** The path currently on screen. */
export function currentPath(): string {
  return normalizePath(location.pathname);
}

/**
 * Declare "the screen now on display lives at this URL". Called by every screen
 * as it paints; it pushes a history entry only when the address is not already
 * right, so back/forward and deep links do not stack duplicates.
 */
export function page(path: string, title: string, noindex = false): void {
  const target = normalizePath(path);
  if (normalizePath(location.pathname) !== target) {
    // A screen that redirects while we are resolving a URL (an auth gate, a
    // dead game id) replaces that URL instead of burying it in the history.
    const url = target + (dispatching ? "" : location.hash);
    if (dispatching) history.replaceState(null, "", url);
    else history.pushState(null, "", url);
  }
  const full = title ? `${title} · ${opts?.brand() ?? ""}` : (opts?.brand() ?? "");
  document.title = full;
  setCanonical(location.origin + target);
  setRobots(noindex);
  opts?.onPage?.(target, full);
}

/**
 * The server answers every unknown path with index.html so deep links work, so
 * the app's own 404 page has to tell crawlers not to index it.
 */
function setRobots(noindex: boolean): void {
  const el = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (!noindex) { el?.remove(); return; }
  if (el) { el.content = "noindex"; return; }
  const meta = document.createElement("meta");
  meta.name = "robots";
  meta.content = "noindex";
  document.head.append(meta);
}

function setCanonical(href: string): void {
  let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.append(el);
  }
  el.href = href;
  document.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.setAttribute("content", href);
}

/** Paint whatever the current URL asks for. */
export function dispatch(): void {
  const path = currentPath();
  const m = resolve(path);
  dispatching = true;
  try {
    if (m) m.route.render(m.params);
    else opts!.notFound(path);
  } finally {
    dispatching = false;
  }
}

/** Go to a page, as a link click would. */
export function navigate(path: string, replace = false): void {
  const target = normalizePath(path);
  opts?.before?.();
  if (target !== currentPath() && opts?.canLeave && !opts.canLeave(target)) return;
  if (target === currentPath()) {
    dispatch(); // re-entering the page you are on still repaints it
    return;
  }
  if (replace) history.replaceState(null, "", target);
  else history.pushState(null, "", target);
  dispatch();
}

export function start(o: RouterOptions): void {
  opts = o;

  // Real links: plain left-clicks are routed, everything else (new tab, middle
  // click, download, external host) is left to the browser.
  document.addEventListener("click", (e) => {
    const ev = e as MouseEvent;
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    const a = (ev.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
    const href = a.getAttribute("href") ?? "";
    if (!href.startsWith("/") || href.startsWith("//")) return;
    // Same-origin, but not this app's: /api/* belongs to the server. Routing it
    // would render a 404 screen instead of leaving (the sign-in handoff to
    // Google is a real navigation to /api/account/google/start).
    if (href.startsWith("/api/")) return;
    ev.preventDefault();
    navigate(href);
  });

  window.addEventListener("popstate", () => {
    const to = currentPath();
    if (opts?.canLeave && !opts.canLeave(to)) {
      // Refused: put the address bar back where the screen actually is.
      history.pushState(null, "", stuckAt || "/");
      return;
    }
    dispatch();
  });

  dispatch();
}

/**
 * Where the user is pinned while a screen refuses to be left (the live board).
 * Set by the app; used to restore the URL after a blocked back-button press.
 */
let stuckAt = "";
export function setStuckPath(path: string): void {
  stuckAt = normalizePath(path);
}
